import React, { HTMLAttributes, SyntheticEvent, useCallback, useEffect, useState } from "react";

import { IonImg } from "@ionic/react";

import classNames from "classnames/bind";
import styles from "./SmartImage.module.scss";
import { getAppConfig } from "@core/config";
import Images from "@core/images";
import { ImageType, ProductImageTypes } from "@core/types";
import { flushSync } from "react-dom";

type ProductThumbnailImageProps = {
  src?: HTMLIonImgElement["src"];
  image?: ImageType;
  alt?: HTMLIonImgElement["alt"];
  smartLoading?: boolean;
  lazyLoad?: boolean;
  isImageLoadedFn?: (isPlaceholder: boolean) => void;
} & HTMLAttributes<HTMLImageElement> &
  HTMLAttributes<HTMLIonImgElement>;

const cx = classNames.bind(styles);
const changeHost = (src: string): string => {
  const CDN_DOMAIN = getAppConfig().CDNDomain;
  if (!CDN_DOMAIN) return src;
  try {
    const url = new URL(src);
    url.host = CDN_DOMAIN;
    return url.href;
  } catch {
    return src;
  }
};

const processImgSrc = (src: string, resize: boolean = true): string => {
  const dotSymbolIndex = src.lastIndexOf(".");
  const resized = resize ? src.substring(0, dotSymbolIndex) + "-300x300" + src.substring(dotSymbolIndex) : src;
  return changeHost(resized);
};

const defaultPlaceholder = Images.placeholderLogo;

const SmartImage = ({
  image,
  lazyLoad,
  className,
  smartLoading,
  src = image?.src ?? "",
  placeholder = defaultPlaceholder,
  isImageLoadedFn,
  ...imgProps
}: ProductThumbnailImageProps) => {
  const handleImgError = useCallback(
    (e: SyntheticEvent<HTMLImageElement | HTMLIonImgElement>) => {
      e.currentTarget.src = placeholder;
      isImageLoadedFn?.(false);
    },
    [isImageLoadedFn, placeholder]
  );

  const [imageUrl, setImageUrl] = useState(processImgSrc(placeholder!, false));

  const imageType = image ? image.type : ProductImageTypes.img;

  useEffect(() => {
    if (imageType === ProductImageTypes.base64) {
      return;
    }

    const isImageNotPlaceholder = (imgSrc: string) => imgSrc !== placeholder;

    const smallImg = new window.Image();
    smallImg.onload = function () {
      flushSync(() => {
        setImageUrl(smallImg.src);
        isImageLoadedFn?.(isImageNotPlaceholder(smallImg.src));
      });
    };
    smallImg.src = src ? processImgSrc(src, true) : placeholder;

    const fullImg = new window.Image();
    fullImg.onload = function () {
      setImageUrl(fullImg.src);
      isImageLoadedFn?.(isImageNotPlaceholder(fullImg.src));
    };

    fullImg.src = src ? processImgSrc(src, false) : placeholder;
  }, [src, image, imageType, placeholder, isImageLoadedFn]);

  const getImgSrc = useCallback(() => {
    return src?.length && imageType === ProductImageTypes.base64 ? `data:image/jpeg;base64, ${src}` : imageUrl;
  }, [src, imageType, imageUrl]);

  return lazyLoad ? (
    <IonImg src={getImgSrc()} className={cx(className, "Image")} onError={handleImgError} {...imgProps} />
  ) : (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img src={getImgSrc()} className={cx(className, "Image")} onError={handleImgError} {...imgProps} />
  );
};

export { SmartImage };
